import Navigo, { type Match, type NavigateOptions } from "navigo";

export type WMURouteContext = {
  path: string;
  match: Match | null;
};

export type RouteHandler = (context: WMURouteContext) => void;

export type NavigateConfig = {
  replace?: boolean;
  stateObj?: object;
  title?: string;
};

export type WMURouterInstance = {
  onAny: (handler: RouteHandler) => WMURouterInstance;
  navigate: (path: string, options?: NavigateConfig) => void;
  resolve: (path?: string) => false | Match;
  currentPath: () => string;
  updatePageLinks: () => void;
  destroy: () => void;
};

export type WMURouterAPI = {
  createHashRouter: () => WMURouterInstance;
  createHistoryRouter: () => WMURouterInstance;
  normalizeHashPath: typeof normalizeHashPath;
  normalizeHistoryPath: typeof normalizeHistoryPath;
};

type RouterMode = "hash" | "history";

function normalizeHashPath(path: string): string {
  const raw = String(path || "").trim().replace(/^#/, "");
  if (!raw) return "/";
  if (raw.startsWith("/")) return raw || "/";
  return `/${raw}`;
}

function normalizeHistoryPath(path: string): string {
  const raw = String(path || "").trim();
  if (!raw) return "/";
  if (raw.startsWith("/")) return raw;
  return `/${raw}`;
}

function wrapHandler(
  handler: RouteHandler,
  readCurrentPath: () => string,
  normalize: (path: string) => string,
): (match?: Match) => void {
  return (match?: Match) => {
    const nextPath = match?.url ? normalize(match.url) : normalize(readCurrentPath());
    handler({
      path: nextPath,
      match: match || null,
    });
  };
}

function toNavigateOptions(options?: NavigateConfig): NavigateOptions {
  return {
    historyAPIMethod: options?.replace ? "replaceState" : "pushState",
    stateObj: options?.stateObj,
    title: options?.title,
  };
}

function buildRouter(mode: RouterMode): WMURouterInstance {
  const isHash = mode === "hash";
  const normalize = isHash ? normalizeHashPath : normalizeHistoryPath;
  const readCurrentPath = () => (
    isHash
      ? normalizeHashPath(window.location.hash || "/")
      : normalizeHistoryPath(`${window.location.pathname}${window.location.search}${window.location.hash}`)
  );
  const router = new Navigo("/", isHash ? { hash: true } : undefined);

  const api: WMURouterInstance = {
    onAny(handler: RouteHandler) {
      router.on(/.*/, wrapHandler(handler, readCurrentPath, normalize));
      return api;
    },
    navigate(path: string, options?: NavigateConfig) {
      router.navigate(normalize(path), toNavigateOptions(options));
    },
    resolve(path?: string) {
      return router.resolve(path ? normalize(path) : undefined, isHash ? { hash: true } : undefined) as false | Match;
    },
    currentPath() {
      return readCurrentPath();
    },
    updatePageLinks() {
      router.updatePageLinks();
    },
    destroy() {
      router.destroy();
    },
  };

  return api;
}

const routerAPI: WMURouterAPI = {
  createHashRouter(): WMURouterInstance {
    return buildRouter("hash");
  },
  createHistoryRouter(): WMURouterInstance {
    return buildRouter("history");
  },
  normalizeHashPath,
  normalizeHistoryPath,
};

window.WMURouter = routerAPI;

export { routerAPI, normalizeHashPath, normalizeHistoryPath };
